import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  TableActionTypes,
  TableState,
  Typography,
} from 'web-library';
import React, { useEffect } from 'react';
import { Theme as AugmentedTheme, useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { IconName } from 'web-library/stories/atom/Icon/Icon';

import {
  ActionType,
  ChangeSummary,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import requestStatusPill from 'web-library/stories/organism/Pill/RequestStatusPill';
import { stringToLongDateAndShortTime } from '../utils/DateUtils';

const useStyles = makeStyles()((theme) =>
({
  titleContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialog: {
    overflow: 'hidden',
  },
  datatable: {
    marginBottom: theme.spacing(5),
  },
  updatedByText: {
    color: theme.color.text.textTertiary,
  },
  changeSummaryTitle: {
    color: theme.color.text.textTertiary,
    '&:nth-child(3), &:nth-child(4)': {
      marginTop: theme.spacing(2),
    },
  },
}));

const tableColumns = (
  state: TableState,
  t: TFunction<'translation'>,
  classes: Record<string,string>
): MUIDataTableColumnDef[] => [
    {
      name: 'dateTime',
      label: t('admin->>UserGroups.DateTime'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '25%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">
              {stringToLongDateAndShortTime(state.data[dataIndex].dateTime)}
            </Typography>
            <Typography
              className={classes.updatedByText}
              variant="subtitle2"
            >{`by ${state.data[dataIndex].userName}`}</Typography>
          </>
        ),
      },
    },
    {
      name: 'items',
      label: t('admin->>UserGroups.Items'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '25%' } }),
        customBodyRenderLite: (dataIndex: number) => <>{state.data[dataIndex].item}</>,
      },
    },
    {
      name: 'changeSummary',
      label: t('admin->>UserGroups.ChangeSummary'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '35%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { changeSummary, actionType } = state.data[dataIndex];

          if (changeSummary?.length) {
            return changeSummary.map((change: ChangeSummary) => (
              <>
                <Typography className={classes.changeSummaryTitle} variant="subtitle2">
                  {change.baseFieldName}
                </Typography>
                {actionType === ActionType.CREATE && (
                  <Typography variant="body2">{change.after}</Typography>
                )}
                {actionType === ActionType.DELETE && (
                  <Typography variant="body2">{change.before}</Typography>
                )}
                {change.itemType === 'TEXT' && (
                  <Typography variant="body2">{change.after}</Typography>
                )}
                {actionType === ActionType.EDIT && (
                  <>
                    <Typography variant="body2">{`Old value: ${change.before}`}</Typography>
                    <Typography variant="body2">{`New value: ${change.after}`}</Typography>
                  </>
                )}
              </>
            ));
          }
          return '-';
        },
      },
    },
    {
      name: 'status',
      label: t('admin->>UserGroups.Status'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '25%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { status } = state.data[dataIndex];

          return requestStatusPill(status);
        },
      },
    },
  ];

interface TransactionLogModalDetailsProps {
  dataTableHook: {
    state: TableState;
    dispatch: React.Dispatch<TableActionTypes>;
    tOptions: MUIDataTableOptions;
    selectionToolbar: (
      bulkActions: React.ReactNode[] | undefined,
      onClearSelection?: (() => void) | undefined
    ) => JSX.Element;
    remoteTotalRowsSelected: number;
  };
  refetch: () => void;
  openModalState: boolean;
  setOpenModalState: React.Dispatch<React.SetStateAction<boolean>>;
}

const TransactionLogModalDetails = ({
  dataTableHook,
  refetch,
  openModalState,
  setOpenModalState,
}: TransactionLogModalDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const theme = useTheme();

  const { dispatch: tableDispatch, state, tOptions } = dataTableHook;

  useEffect(() => {
    refetch();
  }, [refetch, state.page, state.size]);
  const handleCloseTransactionLogModal = () => setOpenModalState(false);

  return (
    <Dialog
      id="group-withdrawal-add-candidates-dialog"
      fullWidth
      open={openModalState}
      maxWidth="md"
      PaperProps={{ className: classes.dialog }}
    >
      <DialogTitle>
        <Box className={classes.titleContent}>
          <Typography variant="h4"> {t('admin->>UserGroups.TransactionLog')}</Typography>
          <IconButton
            name={IconName.cross}
            color={theme.color.icon.iconDefault}
            ariaLabel="mobile menu"
            onClick={handleCloseTransactionLogModal}
            iconSize="L" />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.datatable}>
          <MUIDataTable
            state={state}
            dispatch={tableDispatch}
            columns={tableColumns(state, t, classes)}
            data={state.data}
            emptyDataLabel={t('announcementsNotifications->>SendNotification.NoRecipientMessage')}
            options={{
              ...tOptions,
              pagination: true,
              sort: false,
              selectToolbarPlacement: 'none',
            }}
            rowsPerPageOptions={[5, 10, 20, 40, 50]}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionLogModalDetails;
